// File: PictureList.jsx
import React, {useCallback, useEffect, useRef, useState} from 'react';
import 'styles/pictures/PictureList.css';
import PictureItem from 'components/pictures/display/PictureItem';
import {useLocation, useNavigate} from 'react-router-dom';

const PictureList = ({pictureMetadata, listType, initialIndex = -1}) => {
    const ITEMS_PER_BATCH = 2;
    const INITIAL_ITEMS = 3;

    const realInitialIndex = Math.max(0, initialIndex);
    const [allPictures, setAllPictures] = useState([]);
    const allPicturesRef = useRef(allPictures);

    const [displayedPictures, setDisplayedPictures] = useState([]);
    const displayedPicturesRef = useRef(displayedPictures);

    const [isLoadingDown, setIsLoadingDown] = useState(false);
    const [isLoadingUp, setIsLoadingUp] = useState(false);
    const pendingImageIdsDownRef = useRef(new Set());
    const pendingImageIdsUpRef = useRef(new Set());

    const [downImagesLoaded, setDownImagesLoaded] = useState(false);
    const [upImagesLoaded, setUpImagesLoaded] = useState(false);

    const navigate = useNavigate();
    const isOnTopRef = useRef(true);
    const addingToTopRef = useRef(false);

    const scrollBottomPosition = useRef(0);
    const loadMoreObserverDown = useRef();
    const loadMoreObserverUp = useRef();

    // Create a ref to store a mapping from picture IDs to their global indices
    const picIdToIndexMapRef = useRef(new Map());

    const [isMoreUp, setIsMoreUp] = useState(false);
    const [isMoreDown, setIsMoreDown] = useState(false);

    const {pathname} = useLocation();

    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
            console.log('[scroll] Disabled browser scroll restoration');
        }
    }, [pathname]);

    useEffect(() => {
        console.log('[scroll] effect[] init');

        const handleScroll = () => {
            scrollBottomPosition.current = getScrollPositionFromBottom();
            console.log('[scroll] handleScroll.scrollBottomPosition: ', scrollBottomPosition.current);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);


    useEffect(() => {
        if (!displayedPictures.length || !pictureMetadata.length) {
            setIsMoreUp(false);
            setIsMoreDown(false);
            return;
        }

        const localIsMoreUp = displayedPictures[0].id !== pictureMetadata[0].id;
        const localIsMoreDown = displayedPictures[displayedPictures.length - 1].id !== pictureMetadata[pictureMetadata.length - 1].id;
        setIsMoreUp(localIsMoreUp);
        setIsMoreDown(localIsMoreDown);
        console.log('[scroll] effect[allPictures, displayedPictures] isMoreUp:', displayedPictures[0].id, ' !== ', pictureMetadata[0].id, ' => ', localIsMoreUp);
        console.log('[scroll] effect[allPictures, displayedPictures] isMoreDown:', displayedPictures[displayedPictures.length - 1].id, ' !== ', pictureMetadata[pictureMetadata.length - 1].id, ' => ', localIsMoreDown);
    }, [allPictures, displayedPictures]);

    useEffect(() => {
        setAllPictures(pictureMetadata);
        allPicturesRef.current = pictureMetadata;

        // Build a map from picture IDs to their global indices
        const picIdToIndexMap = new Map();
        pictureMetadata.forEach((pic, idx) => {
            picIdToIndexMap.set(pic.id, idx);
        });
        picIdToIndexMapRef.current = picIdToIndexMap;

        const initialBatchStart = Math.max(0, realInitialIndex);
        const initialBatchEnd = Math.min(pictureMetadata.length, realInitialIndex + INITIAL_ITEMS);
        const initialBatch = pictureMetadata.slice(initialBatchStart, initialBatchEnd);

        const oldDisplayedPictureIds = displayedPicturesRef.current.map((pic) => pic.id);

        setDisplayedPictures(initialBatch);
        displayedPicturesRef.current = initialBatch;

        // Split initial batch into up and down batches
        const upBatch = new Set(pictureMetadata.slice(initialBatchStart, realInitialIndex + 1).map((pic) => pic.id));
        const downBatch = new Set(pictureMetadata.slice(realInitialIndex + 1, initialBatchEnd).map((pic) => pic.id));

        oldDisplayedPictureIds.forEach((pic) => {
            upBatch.delete(pic);
            downBatch.delete(pic);
            }
        );

        // Initialize pending images for the initial batch
        pendingImageIdsUpRef.current = upBatch;
        pendingImageIdsDownRef.current = downBatch;

        setUpImagesLoaded(pendingImageIdsUpRef.current.size === 0);
        setDownImagesLoaded(pendingImageIdsDownRef.current.size === 0);

        console.log('[scroll] effect[pictureMetadata, realInitialIndex] Initial batch:', initialBatch);
    }, [pictureMetadata, realInitialIndex]);

    const loadMorePicturesDown = () => {
        if (isLoadingDown) return; // Prevent multiple loads
        setIsLoadingDown(true);
        const lastDisplayedIndex = allPicturesRef.current.findIndex(
            (pic) => pic.id === displayedPicturesRef.current[displayedPicturesRef.current.length - 1].id
        );
        if (lastDisplayedIndex >= allPicturesRef.current.length - 1) {
            setIsLoadingDown(false);
            return;
        }
        const nextBatchEnd = Math.min(allPicturesRef.current.length, lastDisplayedIndex + 1 + ITEMS_PER_BATCH);
        const nextBatch = allPicturesRef.current.slice(lastDisplayedIndex + 1, nextBatchEnd);
        displayedPicturesRef.current = [...displayedPicturesRef.current, ...nextBatch];
        setDisplayedPictures(displayedPicturesRef.current);

        // Update pending images for the new batch
        console.log('[scroll] Adding images down on batch:', nextBatch);
        pendingImageIdsDownRef.current = new Set(nextBatch.map((pic) => pic.id));
        setDownImagesLoaded(false);
        setIsLoadingDown(false);
    };

    const loadMorePicturesUp = () => {
        if (isLoadingUp) {
            return;
        }
        if (!displayedPicturesRef.current[0]) {
            return;
        }
        setIsLoadingUp(true);
        const firstDisplayedIndex = allPicturesRef.current.findIndex((pic) => pic.id === displayedPicturesRef.current[0].id);
        if (firstDisplayedIndex <= 0) {
            setIsLoadingUp(false);
            return;
        }

        scrollBottomPosition.current = getScrollPositionFromBottom();

        const previousBatchStart = Math.max(0, firstDisplayedIndex - ITEMS_PER_BATCH);
        const previousBatch = allPicturesRef.current.slice(previousBatchStart, firstDisplayedIndex);

        addingToTopRef.current = true;

        console.log('[scroll] Adding to top:', previousBatch);

        displayedPicturesRef.current = [...previousBatch, ...displayedPicturesRef.current];
        setDisplayedPictures(displayedPicturesRef.current);

        // Update pending images for the new batch
        pendingImageIdsUpRef.current = new Set(previousBatch.map((pic) => pic.id));
        setUpImagesLoaded(false);
        setIsLoadingUp(false);
    };

    const handleImageLoad = useCallback(
        (picId) => {
            console.log('[scroll] Image loaded:', picId);

            pendingImageIdsUpRef.current.delete(picId);
            if (pendingImageIdsUpRef.current.size === 0) {
                setUpImagesLoaded(true);
            }

            pendingImageIdsDownRef.current.delete(picId);
            console.log('[scroll] ', pendingImageIdsDownRef.current.size, ' State after ', picId + ' is loaded: ', pendingImageIdsDownRef.current);
            if (pendingImageIdsDownRef.current.size === 0) {
                setDownImagesLoaded(true);
            }

        },
        [] // Empty dependencies to prevent unnecessary re-renders
    );

    const getScrollPositionFromBottom = () => {
        const scrollHeight = document.documentElement.scrollHeight;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const clientHeight = document.documentElement.clientHeight;

        console.log('[scroll] getScrollPositionFromBottom pageHeight:', scrollHeight, ' insisibleTop:', scrollTop, ' screenHeight:', clientHeight);
        return scrollHeight - (scrollTop + clientHeight);
    };

    const setLoadMoreObserverElementDown = useCallback(
        (element) => {
            if (loadMoreObserverDown.current) {
                loadMoreObserverDown.current.disconnect();
            }
            if (element) {
                loadMoreObserverDown.current = new IntersectionObserver((entries) => {
                    if (entries[0].isIntersecting && !isLoadingDown && downImagesLoaded) {
                        loadMorePicturesDown();
                    }
                });
                loadMoreObserverDown.current.observe(element);
            }
        },
        [isLoadingDown, downImagesLoaded]
    );

    const setLoadMoreObserverElementUp = useCallback(
        (element) => {
            if (loadMoreObserverUp.current) {
                loadMoreObserverUp.current.disconnect();
            }
            if (element) {
                loadMoreObserverUp.current = new IntersectionObserver((entries) => {
                    const entry = entries[0];
                    const isBottomVisible = entry.isIntersecting;

                    if (isBottomVisible && !isLoadingUp && upImagesLoaded) {
                        loadMorePicturesUp();
                    }

                    // Update isOnTopRef based on whether the top edge is visible
                    isOnTopRef.current = isBottomVisible;
                });

                loadMoreObserverUp.current.observe(element);
            }
        },
        [isLoadingUp, upImagesLoaded]
    );

    useEffect(() => {
        const adjustScrollPosition = () => {
            const isAddingToTop = addingToTopRef.current;
            addingToTopRef.current = false;
            console.log('[scroll] useEffect[displayedPictures], isAddingToTop:', isAddingToTop, ' | isOnTopRef.current:', isOnTopRef.current);
            if (isOnTopRef.current && isAddingToTop) {
                requestAnimationFrame(() => {
                    const newScrollTop = document.documentElement.scrollHeight - scrollBottomPosition.current - window.innerHeight;
                    console.log('[scroll] useEffect[displayedPictures] Jumping to top:', newScrollTop);
                    window.scrollTo(0, newScrollTop);
                });
            }
            if (!isAddingToTop) {
                requestAnimationFrame(() => {
                    scrollBottomPosition.current = getScrollPositionFromBottom();
                    console.log('[scroll] useEffect[displayedPictures] Bottom image added, recalculating distance:', scrollBottomPosition.current);
                });
            }
        };

        adjustScrollPosition();
    }, [displayedPictures]);

    const onPictureClick = (picId) => {
        navigate(`/image/${listType.toLowerCase()}/${picId}`);
    };

    return (
        <div className={'position-relative'}>
            {isMoreUp && (
                <div className="loading-indicator">Loading...</div>
            )}
            {displayedPictures.map((pic, index) => {
                // Get the global index of the picture
                const globalIndex = picIdToIndexMapRef.current.get(pic.id);

                return (
                    <div
                        className={`picture-in-list ${globalIndex === initialIndex ? 'highlight' : ''}`}
                        key={pic.id}
                        ref={(element) => {
                            if (index === 0) {
                                setLoadMoreObserverElementUp(element);
                            }
                            if (index === displayedPictures.length - 1) {
                                setLoadMoreObserverElementDown(element);
                            }
                        }}
                    >
                        <PictureItem
                            pic={pic}
                            index={globalIndex} // Pass the global index here
                            listType={listType}
                            onImageLoad={handleImageLoad}
                            onPictureClick={onPictureClick}
                        />
                    </div>
                );
            })}
            {isMoreDown && (
                <div className="loading-indicator">Loading...</div>
            )}
        </div>
    );
};

export default PictureList;
